import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Receipt, Add, Trash } from "iconsax-react";
import { ModalForm, Input, DatePicker, Button, ToggleButton, Prompt } from "@/shared/ui";
import { extractErrorMessage, formatMoneyKzt, type Locale } from "@/shared/utils";
import { useSettleBusinessTripMutation } from "../../../api";
import { settleTripSchema, extractVat, type SettleTripFormValues } from "../../../utils/settlementValidation";

interface Props {
  tripId: number;
  amountIssued: string;
  perDiemRate: string | null;
  defaultDays: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export default function TripSettlementForm({ tripId, amountIssued, perDiemRate, defaultDays, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("AdvancesPage");
  const locale = (i18n.language as Locale) || "ru";
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [settleTrip, { isLoading }] = useSettleBusinessTripMutation();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SettleTripFormValues>({
    resolver: zodResolver(settleTripSchema),
    defaultValues: {
      days: defaultDays,
      transport: [],
      lodging_gross: "",
      lodging_vendor: "",
      lodging_act_no: "",
      lodging_act_date: null,
      lodging_has_esf: false,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "transport" });
  const days = watch("days");
  const transport = watch("transport");
  const lodgingGrossRaw = watch("lodging_gross");
  const lodgingHasEsf = watch("lodging_has_esf");

  const perDiemRateNum = Number(perDiemRate) || 0;
  const perDiemTotal = perDiemRateNum * (days || 0);
  const transportGross = (transport || []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const transportVat = (transport || []).reduce(
    (sum, line) => sum + (line.has_esf ? extractVat(Number(line.amount) || 0) : 0),
    0,
  );
  const lodgingGross = Number(lodgingGrossRaw) || 0;
  const lodgingVat = lodgingHasEsf ? extractVat(lodgingGross) : 0;
  const spent = perDiemTotal + transportGross + lodgingGross;
  const vatTotal = transportVat + lodgingVat;
  const issuedNum = Number(amountIssued) || 0;
  const diff = issuedNum - spent;

  async function onSubmit(data: SettleTripFormValues) {
    if (isLoading) return;
    try {
      const lodgingNum = Number(data.lodging_gross) || 0;
      await settleTrip({
        id: tripId,
        days: data.days,
        transport: data.transport.map(function buildTransport(line) {
          return {
            vendor: line.vendor || undefined,
            doc_no: line.doc_no || undefined,
            date: line.date ? line.date.toISOString().split("T")[0] : null,
            amount: line.amount,
            has_esf: line.has_esf,
          };
        }),
        lodging:
          lodgingNum > 0
            ? {
                gross: data.lodging_gross as string,
                vendor: data.lodging_vendor || undefined,
                act_no: data.lodging_act_no || undefined,
                act_date: data.lodging_act_date ? data.lodging_act_date.toISOString().split("T")[0] : null,
                has_esf: data.lodging_has_esf,
              }
            : undefined,
      }).unwrap();
      setPrompt({
        title: t("settlement.messages.successTitle"),
        text: t("settlement.messages.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("settlement.messages.errorTitle"),
        text: extractErrorMessage(err, "settlement.messages.errorText", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) onSuccess();
  }

  return (
    <>
      <ModalForm
        icon={Receipt}
        onClose={onClose}
        resize={false}
        footer={
          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <Button variant="secondary" size="huge" onClick={onClose} type="button">
              {t("settlement.buttons.cancel")}
            </Button>
            <Button variant="primary" size="huge" type="submit" form="settle-trip-form" disabled={isLoading}>
              {isLoading ? t("settlement.buttons.loading") : t("settlement.buttons.submit")}
            </Button>
          </div>
        }>
        <form id="settle-trip-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
            <h4 className="text-display-2xs content-base-primary">{t("settlement.tripTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("settlement.tripSubtitle")}</p>
          </div>

          <div className="flex-1 overflow-auto flex flex-col gap-4 p-1 my-5 page-scroll">
            <div className="flex flex-col gap-3">
              <p className="text-body-bold-md content-base-primary">{t("settlement.transport")}</p>
              {fields.map(function renderTransport(field, index) {
                return (
                  <div key={field.id} className="flex flex-col gap-3 p-4 radius-md surface-secondary-fill">
                    <div className="flex items-center justify-between">
                      <p className="text-body-bold-md content-base-primary">
                        {t("settlement.transportLine")} {index + 1}
                      </p>
                      <Button variant="danger" size="sm" type="button" onClick={() => remove(index)}>
                        <Trash size={16} color="currentColor" />
                        {t("settlement.buttons.removeTransport")}
                      </Button>
                    </div>
                    <Input
                      label={t("settlement.fields.vendor")}
                      placeholder={t("settlement.placeholders.vendor")}
                      {...register(`transport.${index}.vendor`)}
                    />
                    <Input
                      label={t("settlement.fields.documentNo")}
                      placeholder={t("settlement.placeholders.documentNo")}
                      {...register(`transport.${index}.doc_no`)}
                    />
                    <DatePicker
                      locale={locale}
                      mode="single"
                      label={t("settlement.fields.documentDate")}
                      value={watch(`transport.${index}.date`) ?? null}
                      onChange={(date) => setValue(`transport.${index}.date`, date, { shouldValidate: true })}
                    />
                    <Input
                      label={t("settlement.fields.grossAmount")}
                      placeholder="0"
                      {...register(`transport.${index}.amount`)}
                      error={
                        errors.transport?.[index]?.amount?.message
                          ? t(errors.transport[index]!.amount!.message as string)
                          : undefined
                      }
                    />
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-body-bold-sm content-base-primary">{t("settlement.fields.hasEsf")}</span>
                        <span className="text-body-regular-sm content-base-secondary">{t("settlement.fields.hasEsfHint")}</span>
                      </div>
                      <ToggleButton
                        checked={watch(`transport.${index}.has_esf`) ?? false}
                        onChange={(checked) => setValue(`transport.${index}.has_esf`, checked, { shouldValidate: true })}
                      />
                    </div>
                  </div>
                );
              })}
              <Button
                variant="tertiary"
                size="md"
                type="button"
                onClick={() => append({ vendor: "", doc_no: "", date: null, amount: "", has_esf: false })}>
                <Add size={16} color="currentColor" />
                {t("settlement.buttons.addTransport")}
              </Button>
            </div>

            <div className="flex flex-col gap-3 pt-2 border-t surface-base-stroke">
              <p className="text-body-bold-md content-base-primary">{t("settlement.lodging")}</p>
              <Input label={t("settlement.fields.lodgingGross")} placeholder="0" {...register("lodging_gross")} />
              <Input
                label={t("settlement.fields.vendor")}
                placeholder={t("settlement.placeholders.lodgingVendor")}
                {...register("lodging_vendor")}
              />
              <Input
                label={t("settlement.fields.lodgingActNo")}
                placeholder={t("settlement.placeholders.lodgingActNo")}
                {...register("lodging_act_no")}
              />
              <DatePicker
                locale={locale}
                mode="single"
                label={t("settlement.fields.lodgingActDate")}
                value={watch("lodging_act_date") ?? null}
                onChange={(date) => setValue("lodging_act_date", date, { shouldValidate: true })}
              />
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-body-bold-sm content-base-primary">{t("settlement.fields.hasEsf")}</span>
                  <span className="text-body-regular-sm content-base-secondary">{t("settlement.fields.hasEsfHint")}</span>
                </div>
                <ToggleButton
                  checked={lodgingHasEsf ?? false}
                  onChange={(checked) => setValue("lodging_has_esf", checked, { shouldValidate: true })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 p-4 radius-md surface-secondary-fill">
              <div className="flex flex-col gap-0.5">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.perDiem")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(perDiemTotal))}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.vat")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(vatTotal))}</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 p-4 radius-md surface-secondary-fill">
              <div className="flex items-center justify-between">
                <span className="text-body-regular-md content-base-secondary">{t("settlement.summary.spent")}</span>
                <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(String(spent))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-regular-md content-base-secondary">{t("settlement.summary.advanceAmount")}</span>
                <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(String(issuedNum))}</span>
              </div>
              {diff < 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-body-regular-md content-base-secondary">{t("settlement.summary.overspendLabel")}</span>
                  <span className="text-body-bold-md content-action-notice">{formatMoneyKzt(String(Math.abs(diff)))}</span>
                </div>
              )}
              {diff > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-body-regular-md content-base-secondary">{t("settlement.summary.refundLabel")}</span>
                  <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(String(diff))}</span>
                </div>
              )}
            </div>
          </div>
        </form>
      </ModalForm>

      {isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("settlement.messages.loading")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="AdvancesPage"
        />
      )}
    </>
  );
}
